/**
 * Green Mitt Kit — before & after photo submission.
 *
 * Deliberately does NOT fake a success state. With no endpoint configured the
 * photo slots and previews still work, but the submit button is disabled and
 * shows the section's pending message. With an endpoint configured, the success
 * panel appears only after that endpoint actually returns 2xx.
 *
 * Photos are shrunk in the browser before they are sent. The endpoint runs on
 * Vercel, which rejects any request body over 4.5 MB before our code sees it,
 * so each picked photo is decoded, downscaled to a 1600px longest edge and
 * re-encoded as JPEG. A phone photo lands around 300–600 KB; two of them plus
 * multipart overhead stay well inside the cap. If the browser cannot decode a
 * file (an original HEIC picked from Files on iOS, for instance) the original
 * is used as-is and the 2 MB per-photo check decides.
 */
(function () {
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** Longest edge after downscaling. Never upscales a smaller photo. */
  var MAX_EDGE = 1600;
  var JPEG_QUALITY = 0.82;
  /**
   * Per-photo cap AFTER compression. Two photos at this size plus the multipart
   * envelope stay under Vercel's 4.5 MB body limit with margin. Only a photo
   * that could not be compressed can realistically hit it.
   */
  var MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

  /**
   * Decode `file` into an <img>, draw it downscaled onto a canvas, and hand
   * back a JPEG Blob. Rejects on anything the browser can't do (undecodable
   * bytes, no 2D context, toBlob unavailable or returning null) so the caller
   * can fall back to the original file.
   *
   * Orientation: every current browser applies the EXIF orientation while
   * decoding an <img> (image-orientation: from-image is the default), and
   * drawImage copies the oriented pixels, so the output is upright.
   */
  function compressImage(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      var finish = function () {
        URL.revokeObjectURL(url);
      };

      img.onload = function () {
        finish();
        try {
          var width = img.naturalWidth || img.width;
          var height = img.naturalHeight || img.height;
          if (!width || !height) {
            reject(new Error('Image has no dimensions'));
            return;
          }
          var scale = Math.min(1, MAX_EDGE / Math.max(width, height));
          var canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(width * scale));
          canvas.height = Math.max(1, Math.round(height * scale));
          var ctx = canvas.getContext('2d');
          if (!ctx || typeof canvas.toBlob !== 'function') {
            reject(new Error('Canvas unavailable'));
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            function (blob) {
              if (blob && blob.size > 0) resolve(blob);
              else reject(new Error('Canvas produced no image'));
            },
            'image/jpeg',
            JPEG_QUALITY
          );
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = function () {
        finish();
        reject(new Error('Image could not be decoded'));
      };
      img.src = url;
    });
  }

  window.GmkPhotos = {
    init: function (rootId) {
      var root = document.getElementById(rootId);
      if (!root || root.dataset.gmkPhotosInitialized) return;
      root.dataset.gmkPhotosInitialized = 'true';

      var endpoint = (root.dataset.gmkEndpoint || '').trim();
      // Guard on the ORIGINAL file, before decoding. A 20 MB phone photo shrinks
      // fine; this only stops something pathological from being decoded at all.
      var maxOriginalBytes = (parseInt(root.dataset.gmkMaxMb, 10) || 15) * 1024 * 1024;
      var pendingMessage = root.dataset.gmkPendingMessage || '';
      // What the status line shows when there is nothing to say.
      var idleMessage = endpoint ? '' : pendingMessage;

      var formWrap = root.querySelector('[data-gmk-photos-form]');
      var successPanel = root.querySelector('[data-gmk-success]');
      var emailInput = root.querySelector('[data-gmk-email]');
      var honeypotInput = root.querySelector('[data-gmk-website]');
      var submitBtn = root.querySelector('[data-gmk-submit]');
      var status = root.querySelector('[data-gmk-status]');
      if (!formWrap || !submitBtn) return;

      function setStatus(text, kind) {
        if (!status) return;
        status.textContent = text || '';
        status.className = 'gmk-photos__status' + (kind ? ' is-' + kind : '');
      }

      /* ---------- Photo slots ---------- */
      var slots = {};

      function clearSlot(record) {
        record.ticket += 1; // abandons any compression still running
        record.pending = false;
        record.blob = null;
        record.name = null;
        if (record.objectUrl) {
          URL.revokeObjectURL(record.objectUrl);
          record.objectUrl = null;
        }
        record.el.classList.remove('is-filled');
      }

      root.querySelectorAll('[data-gmk-slot]').forEach(function (slot) {
        var key = slot.dataset.gmkSlot;
        var record = {
          el: slot,
          input: slot.querySelector('[data-gmk-file]'),
          img: slot.querySelector('[data-gmk-preview]'),
          objectUrl: null,
          /** The bytes that will be uploaded: the compressed JPEG, or the original on fallback. */
          blob: null,
          /** Filename sent with the blob. */
          name: null,
          /** True while a pick is being compressed. */
          pending: false,
          ticket: 0
        };
        slots[key] = record;
        if (!record.input) return;

        record.input.addEventListener('change', function () {
          var file = record.input.files && record.input.files[0];
          if (!file) return;

          if (file.type.indexOf('image/') !== 0) {
            setStatus("That file isn't a photo. Try again with an image.", 'error');
            record.input.value = '';
            return;
          }
          if (file.size > maxOriginalBytes) {
            setStatus(
              'That photo is over ' + Math.round(maxOriginalBytes / (1024 * 1024)) + ' MB. Try a smaller one.',
              'error'
            );
            record.input.value = '';
            return;
          }

          clearSlot(record);
          var ticket = record.ticket;
          record.pending = true;
          setStatus('Preparing your photo…');

          compressImage(file)
            .then(function (blob) {
              return { blob: blob, name: key + '.jpg' };
            })
            .catch(function () {
              // Could not decode or encode (HEIC from Files on iOS, an odd
              // format, an ancient browser). Send the original and let the
              // size check decide.
              return { blob: file, name: file.name || key };
            })
            .then(function (result) {
              if (ticket !== record.ticket) return; // superseded by a newer pick or a retake
              record.pending = false;

              if (result.blob.size > MAX_UPLOAD_BYTES) {
                record.input.value = '';
                setStatus(
                  'That photo is too large even after compression — try a different shot.',
                  'error'
                );
                return;
              }

              record.blob = result.blob;
              record.name = result.name;
              record.objectUrl = URL.createObjectURL(result.blob);
              if (record.img) record.img.src = record.objectUrl;
              record.el.classList.add('is-filled');
              setStatus(idleMessage);
            });
        });

        var retake = slot.querySelector('[data-gmk-retake]');
        if (retake) {
          retake.addEventListener('click', function () {
            record.input.value = '';
            clearSlot(record);
            setStatus(idleMessage);
            record.input.click();
          });
        }
      });

      /* ---------- Submit ---------- */

      // No endpoint yet: the slots above still work so the flow can be reviewed
      // and a visitor can see what is coming, but the button is disabled and
      // points them somewhere that actually reaches us. Wiring the submit
      // handler at all would risk confirming a submission that went nowhere.
      if (!endpoint) {
        submitBtn.disabled = true;
        setStatus(pendingMessage);
        return;
      }

      function slotRecord(key) {
        return slots[key] || null;
      }

      submitBtn.addEventListener('click', function () {
        var before = slotRecord('before');
        var after = slotRecord('after');
        var email = emailInput ? emailInput.value.trim() : '';

        if ((before && before.pending) || (after && after.pending)) {
          setStatus('Still preparing your photos — one moment.');
          return;
        }

        var hasBefore = !!(before && before.blob);
        var hasAfter = !!(after && after.blob);

        if (!hasBefore && !hasAfter) {
          setStatus('Add your before and after photos first.', 'error');
          return;
        }
        if (!hasBefore) {
          setStatus('Add your before photo too.', 'error');
          return;
        }
        if (!hasAfter) {
          setStatus('Add your after photo too, so we can see the difference.', 'error');
          return;
        }
        if (!EMAIL_RE.test(email)) {
          setStatus('Enter a valid email so we can reply.', 'error');
          if (emailInput) emailInput.focus();
          return;
        }

        var payload = new FormData();
        payload.append('before', before.blob, before.name);
        payload.append('after', after.blob, after.name);
        payload.append('email', email);
        // Honeypot. A real visitor never sees the field, so it is sent empty;
        // the endpoint quietly drops anything that fills it.
        payload.append('website', honeypotInput ? honeypotInput.value : '');

        submitBtn.disabled = true;
        setStatus('Sending your photos…');

        fetch(endpoint, { method: 'POST', body: payload })
          .then(function (response) {
            if (!response.ok) throw new Error('Upload failed with status ' + response.status);
            formWrap.hidden = true;
            if (successPanel) {
              successPanel.hidden = false;
              successPanel.setAttribute('tabindex', '-1');
              successPanel.focus({ preventScroll: true });
            }
          })
          .catch(function () {
            submitBtn.disabled = false;
            setStatus("That didn't send. Check your connection and try again.", 'error');
          });
      });
    }
  };
})();
